import { GetServerSideProps } from 'next'
import Head from 'next/head'
import { plainToInstance } from 'class-transformer'
import { gqlApiInstance } from '@client/init'

import { StoryPage } from '@features/story/pages/StoryPage'
import { StoryProps } from '@features/story/pages/StoryPage/interface'
import { StoryReaderProvider } from '@hooks/book/useStoryReader'
import { AppLayout } from '@components/layouts/AppLayout'
import { GET_STORY_FOR_SHARE } from '@client/collections/Story/schemas/getStoryForShare'
import { StoryForShareType } from '@models/story/StoryForShareType'
import { ErrorMessage } from '@components/ErrorMessage'
import { BASE_URL } from '@configs/config'
import { getErrorMessage } from '@lib/utils'
import { isMaintenanceHandler } from '@lib/maintenanceHandler'

function Story({
  bookId,
  title,
  cover,
  categoryName,
  firstPenName,
  coverImgPath,
  facebookShareImageUrl,
  twitterShareImageUrl,
  tags,
  errorMessage,
}: StoryProps) {
  if (errorMessage)
    return (
      <AppLayout>
        <ErrorMessage
          message={
            errorMessage === `Book #${bookId} not found`
              ? 'เรื่องนี้ถูกปิดการเผยแพร่แล้ว'
              : errorMessage
          }
          redirectUrl='/'
        />
      </AppLayout>
    )

  return (
    <AppLayout
      seoDescription={`${title} ${cover}`}
      appendKeyword={` ${title} ${tags} ${categoryName}`}
    >
      <Head>
        <title>{`MaReads มารี้ด | ${title} ${categoryName} ${firstPenName}`}</title>
        <meta property='og:url' content={`${BASE_URL}/story/${bookId}`} />
        <meta property='og:title' content={title} />
        <meta property='og:description' content={cover} />
        <meta property='og:type' content='article' />
        <meta
          property='og:image'
          content={facebookShareImageUrl || coverImgPath}
        />
        <meta
          property='twitter:image'
          content={twitterShareImageUrl || coverImgPath}
        />
        <meta name='twitter:card' content='summary_large_image' />
      </Head>
      <StoryReaderProvider bookId={bookId}>
        <StoryPage />
      </StoryReaderProvider>
    </AppLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
  const bookId = Number(params?.id)

  try {
    const isMaintenance = await isMaintenanceHandler()
    if (isMaintenance) {
      return {
        redirect: {
          destination: '/maintenance',
          permanent: false,
        },
        props: {
          isMaintenance: !!isMaintenance,
        },
      }
    }

    const { getBookReader } = await gqlApiInstance.request(
      GET_STORY_FOR_SHARE,
      {
        getBookReaderId: bookId,
      }
    )
    const storyForShare = plainToInstance(StoryForShareType, getBookReader)

    return {
      props: {
        key: `story-${bookId}`,
        bookId,
        title: storyForShare.title,
        cover: storyForShare.cover,
        categoryName: storyForShare.categoryName,
        firstPenName: storyForShare.firstPenName,
        coverImgPath: storyForShare.coverImgPath,
        facebookShareImageUrl: storyForShare.facebookShareImageUrl,
        twitterShareImageUrl: storyForShare.twitterShareImageUrl,
        tags: storyForShare.tags,
        isMaintenance: false,
      },
    }
  } catch (error: any) {
    if (error.response?.status === 503) {
      return {
        redirect: {
          destination: '/maintenance',
          permanent: false,
        },
        props: {
          isMaintenance: true,
        },
      }
    }

    return {
      props: {
        key: `story-${bookId}`,
        bookId,
        title: '',
        cover: '',
        categoryName: '',
        firstPenName: '',
        coverImgPath: '',
        facebookShareImageUrl: '',
        twitterShareImageUrl: '',
        tags: '',
        errorMessage: getErrorMessage(error),
        isMaintenance: false,
      },
    }
  }
}

export default Story
